/// <amd-module name="Wit/WorkItemForm/Details/WorkItemFormHistoricalComment" />
import { CommentReactionType } from "Comments/Common/Contracts";
import { IComment, ICommentArtifactInfo } from "Comments/Common/Contracts/ICommentsClient";
import { CommentItem } from "Comments/Item/Components/CommentItem";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { css } from "VSSUI/Util";
import * as WorkItemTracking from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CommentFormat } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { getDateInUserTimeZone, tooltipTimeFormat } from "Wit/Common/DateUtils";
import { convertRawMentionsToRichPreviews } from "Wit/HtmlEditor/Utils/Mention";
import { makeAllWorkItemHyperlinksClickable } from "Wit/HtmlEditor/Utils/WorkItemLinks";
import * as React from "react";
import { htmlDecode } from "../Common/HtmlUtils";
import { ImageUploader } from "../Common/ImageUploader";
import { ICommentWithReactions } from "../Common/Services";
import { WorkItemFormCommentPreview, WorkItemFormCommentPreviewProps } from "./WorkItemFormCommentPreview";

export type WorkItemFormHistoricalCommentProps = {
    artifactInfo: ICommentArtifactInfo;
    canEdit: boolean;
    isReactionsEnabled: boolean;
    comment: IComment;
    baseUrl: string;
    key: string;
    imageUploader: ImageUploader;
    commentWithReactions: ICommentWithReactions;
    pageContext: IVssPageContext;
    createCommentReaction: (commentId: number, reactionType: CommentReactionType) => void;
    cancelEdit: (commentId: number, isDirty: boolean) => void;
    deleteComment: (commentId: number) => void;
    deleteCommentReaction: (commentId: number, reactionType: CommentReactionType) => void;
    startEdit: (commentId: number) => void;
    update: (commentId: number, newValue: string, oldValue: string) => void;
    getRecentlyReactedUsers: (commentId: number, reactionType: WorkItemTracking.CommentReactionType, top: number) => void;
    renderPersona: (i: IdentityRef) => JSX.Element;
};

const isMarkdownDiscussionEnabled = (pageContext: IVssPageContext): boolean => {
    return isFeatureFlagEnabled(pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false);
};

const isLinkifyImagesInMarkdownEnabled= (pageContext: IVssPageContext): boolean => {
    return isFeatureFlagEnabled(pageContext, FeatureFlags.LinkifyImageMarkdown, false);
};

export const WorkItemFormHistoricalComment: React.FC<WorkItemFormHistoricalCommentProps> = props => {
    const key = "wit-historical-comment-" + props.comment.id + "-" + props.comment.version;
    return (
        <CommentItem
            artifactInfo={props.artifactInfo}
            canEdit={props.canEdit}
            className={css(
                "displayed-comment",
                isMarkdownDiscussionEnabled(props.pageContext) && "depth-8 markdown-discussion-comment",
                isMarkdownDiscussionEnabled(props.pageContext) && location.hash === "#" + props.comment.id && "highlighted"
            )}
            comment={props.comment}
            baseUrl={props.baseUrl}
            isEditing={false}
            isReactionsEnabled={props.isReactionsEnabled}
            key={key}
            onAddReaction={(commentId: number, reactionType: CommentReactionType, isAddedFromStatusBar: boolean) => {
                props.createCommentReaction(commentId, reactionType);
            }}
            onCancelEdit={(commentId: number, isDirty: boolean) => {
                props.cancelEdit(commentId, isDirty);
            }}
            onConfirmDelete={(commentId: number, confirmed: boolean) => {
                if (confirmed) {
                    props.deleteComment(commentId);
                }
            }}
            onDeleteReaction={(commentId: number, reactionType: CommentReactionType, isDeletedFromStatusBar: boolean) => {
                props.deleteCommentReaction(commentId, reactionType);
            }}
            onGetRecentlyReactedUsers={(commentId, reactionType, top) => {
                props.getRecentlyReactedUsers(commentId, reactionType, top);
            }}
            onPostRenderContent={(content, contentDiv) => {
                makeAllWorkItemHyperlinksClickable(contentDiv, props.pageContext);
                convertRawMentionsToRichPreviews(contentDiv, props.pageContext);
            }}
            onRenderCommentBody={
                props.comment.format?.value == CommentFormat.Markdown
                    ? (commentText: string) =>
                        <WorkItemFormCommentPreviewMemoized content={commentText} linkifyImages={isLinkifyImagesInMarkdownEnabled(props.pageContext)} />
                    : undefined
            }
            onStartEdit={(commentId: number) => {
                props.startEdit(commentId);
            }}
            onUpdate={(commentId: number, newValue: string, oldValue: string) => {
                props.update(commentId, newValue, oldValue);
            }}
            onRenderPersona={i => props.renderPersona(i)}
            readonly={false}
            reactionTypeToInfoMap={props.commentWithReactions.reactions}
            viewerContentClassName="h-scroll-auto"
            tooltipTimeFormat={tooltipTimeFormat}
            currentDate={getDateInUserTimeZone()}
            doNotHighlight={isMarkdownDiscussionEnabled(props.pageContext)}
        />
    );
};

const WorkItemFormCommentPreviewMemoized = (props: {content: string, linkifyImages: boolean}) =>
    React.useMemo(() => {
        const comment = htmlDecode(props.content);
        return <WorkItemFormCommentPreview content={comment} options={{linkifyImages: props.linkifyImages}}/>;
    }, [props.content]);
